import React, { useState } from 'react';
import { FileSignatureIcon, ShieldAlertIcon } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Panel } from '../ui/Panel';
import { authorisations, serviceInstitutions, serviceProviders } from '../../data/services';
import type { ServiceRequest } from '../../types/services';

/**
 * Authorisation replaces credential sharing: a named party, a defined task, an
 * expiry, and a revocable record — never a shared university password.
 */
export function AuthorisationPanel({
  request,
  onUpdate
}: {
  request: ServiceRequest;
  onUpdate: (patch: Partial<ServiceRequest>) => void;
}) {
  const existing = authorisations.find(item => item.id === request.authorisationId);
  const [revoked, setRevoked] = useState(false);
  const [generated, setGenerated] = useState(false);
  const provider = serviceProviders.find(item => item.id === request.providerId);
  const institution = serviceInstitutions.find(item => item.id === request.institutionId);
  const record = existing ?? (generated ? {
    id: 'auth-new',
    purpose: request.purpose,
    authorisedParty: provider?.name ?? 'Named representative',
    institutionId: request.institutionId,
    scope: ['Collect the document named in this request'],
    validUntil: request.deadline ?? '30 days from today',
    signedOn: '14 Aug 2026',
    acknowledgedOn: null,
    revokedOn: null,
    institutionAccepts: 'unconfirmed' as const
  } : null);
  return <Panel title="Authorisation" description="Required because someone will act on your behalf at the institution." data-id="element-4168" data-mp="4r0qju7310ne">
      {!record ? <>
          <p className="text-[13px] leading-6 text-ink-muted" data-id="element-4169" data-mp="dpvxj8hyaerr">
            Generate a written authorisation naming {provider?.name ?? 'your representative'} and the
            single task they may carry out. You can revoke it at any time.
          </p>
          <Button variant="primary" size="sm" className="mt-3" icon={<FileSignatureIcon className="h-3.5 w-3.5" data-id="element-4171" data-mp="1l258vf0za0ga" />} onClick={() => {
        setGenerated(true);
        onUpdate({
          authorisationId: 'auth-new'
        });
      }} data-id="element-4170" data-mp="3i2j68b9itr08">
            Generate authorisation
          </Button>
        </> : <>
          <dl className="grid gap-x-6 gap-y-2.5 sm:grid-cols-2" data-id="element-4172" data-mp="1zuynky8fecjw">
            {[{
          term: 'Purpose',
          value: record.purpose
        }, {
          term: 'Authorised party',
          value: record.authorisedParty
        }, {
          term: 'Institution',
          value: institution?.name ?? '—'
        }, {
          term: 'Valid until',
          value: record.validUntil
        }, {
          term: 'Signed by you',
          value: record.signedOn ?? 'Not yet signed'
        }, {
          term: 'Acknowledged by the party',
          value: record.acknowledgedOn ?? 'Not yet acknowledged'
        }].map(row => <div key={row.term} data-id="element-4173" data-mp="gu7srlm7i0x4">
                <dt className="text-[12px] font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-4174" data-mp="gt68qrn7q439">
                  {row.term}
                </dt>
                <dd className="mt-0.5 text-[13px] leading-6 text-ink" data-id="element-4175" data-mp="bxh5oebzi527">{row.value}</dd>
              </div>)}
          </dl>

          <p className="mt-3 text-[12px] font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-4176" data-mp="25tg6iyzmy3d">
            What is authorised
          </p>
          <ul className="mt-1 space-y-1" data-id="element-4177" data-mp="3tcxn7dv4ukbs">
            {record.scope.map(item => <li key={item} className="text-[13px] leading-6 text-ink" data-id="element-4178" data-mp="3p8g4zj4f8f34">
                ☑ {item}
              </li>)}
            <li className="text-[13px] leading-6 text-ink-muted" data-id="element-4179" data-mp="3kcrdm32k24hw">
              ☐ Nothing else. Any other task needs a separate authorisation.
            </li>
          </ul>

          <div className="mt-3 flex flex-wrap items-center gap-2" data-id="element-4180" data-mp="1ij1mgonsr02z">
            <Badge tone={revoked ? 'danger' : 'ok'} data-id="element-4181" data-mp="1dk739llx39ic">{revoked ? 'Revoked' : 'Active'}</Badge>
            <Badge tone={record.institutionAccepts === 'confirmed' ? 'ok' : 'warn'} data-id="element-4182" data-mp="b6sq8xa2115d">
              {record.institutionAccepts === 'confirmed' ? 'Institution confirmed it accepts this form' : 'Institution acceptance not confirmed'}
            </Badge>
            {!revoked && <Button size="sm" variant="danger" onClick={() => setRevoked(true)} data-id="element-4183" data-mp="66wluwxg37pa">
                Revoke now
              </Button>}
          </div>

          <p className="mt-3 flex items-start gap-2 rounded-lg border border-line bg-canvas px-3.5 py-3 text-[12px] leading-5 text-ink-muted" data-id="element-4184" data-mp="3sf5ydsm9fgxr">
            <ShieldAlertIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warn-700" data-id="element-4185" data-mp="3ep2h7g28mdf6" />
            {institution?.short} has not confirmed to Campuzon that it accepts this form, so treat it as a
            written instruction from you rather than an approved institutional document. Never give the
            representative your university login instead — no legitimate provider will ask.
          </p>
        </>}
    </Panel>;
}